import { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/Switch';
import { getSelection, subscribeTheme, type ThemeSelection } from '@/lib/theme';
import {
  useAppearancePrefs,
  setFontSize,
  setDensity,
  setReducedMotion,
  type FontSize,
  type Density,
} from '@/lib/appearancePrefs';
import {
  useFontPreference,
  setFontPreference,
  FONT_OPTIONS,
  type FontId,
} from '@/lib/fontPreference';
import { SelectMenu, type SelectOption } from '@/components/SelectMenu';
import { Segmented } from '@/components/Segmented';
import { ThemeStudio } from './ThemeStudio';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, type LangId } from '@/i18n';
import { useRailPlacement, setRailPlacement, type RailPlacement } from '@/lib/railPlacement';
import { platformOwnsIdentity } from '@/lib/platformIdentity';

const LANG_SELECT_OPTIONS: SelectOption<LangId>[] = SUPPORTED_LANGUAGES.map((l) => ({
  value: l.id,
  label: l.label,
}));

/**
 * Appearance tab — theme toggle, density, font size, reduced motion.
 * ThemeToggle is the live one (swaps the data-theme attribute); the rest wire
 * into the persisted appearance prefs.
 */
export function AppearanceTab() {
  const { t, i18n } = useTranslation();
  const prefs = useAppearancePrefs();
  const themeSelection = useThemeSelection();
  const font = useFontPreference();
  const fontSelectOptions: SelectOption<FontId>[] = FONT_OPTIONS.map((f) => ({
    value: f.id,
    label: f.label,
  }));
  const currentLang = (i18n.resolvedLanguage ?? 'ru') as LangId;
  // Inside the platform shell (its Chat frame, the Desktop shell) the shell's switch is the one
  // language switch; Bunker follows it.
  const shellOwnsLanguage = platformOwnsIdentity();
  const railPlacement = useRailPlacement();
  return (
    <div className="app-settings-groups app-settings-appearance flex flex-col">
      <h2 className="app-settings-section-title">{t('settings.appearance.sectionLook')}</h2>
      <div
        className="app-settings-appearance-theme py-3"
      >
        <div className="min-w-0 flex-1">
          <div className="text-base font-medium tracking-[-0.01em] text-ink">
            {t('settings.appearance.theme')}
          </div>
        </div>
        <ThemeStudio selection={themeSelection} />
      </div>

      {!shellOwnsLanguage && <div className="app-settings-control-row flex items-center justify-between gap-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-base font-medium tracking-[-0.01em] text-ink">
            {t('settings.appearance.language')}
          </div>
        </div>
        <SelectMenu<LangId>
          ariaLabel={t('settings.appearance.language')}
          value={currentLang}
          options={LANG_SELECT_OPTIONS}
          onChange={(v) => void i18n.changeLanguage(v)}
          className="w-44 shrink-0"
        />
      </div>}

      <div className="app-settings-control-row flex items-center justify-between gap-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-base font-medium tracking-[-0.01em] text-ink">
            {t('settings.appearance.density')}
          </div>
        </div>
        <Segmented<Density>
          value={prefs.density}
          onChange={setDensity}
          options={[
            { value: 'cozy', label: t('settings.appearance.densityCozy') },
            { value: 'compact', label: t('settings.appearance.densityCompact') },
          ]}
        />
      </div>

      <div className="app-settings-control-row flex items-center justify-between gap-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-base font-medium tracking-[-0.01em] text-ink">
            {t('settings.appearance.font')}
          </div>
        </div>
        <SelectMenu<FontId>
          ariaLabel={t('settings.appearance.font')}
          value={font}
          options={fontSelectOptions}
          onChange={setFontPreference}
          className="w-44 shrink-0"
        />
      </div>

      <h2 className="app-settings-section-title">{t('settings.appearance.sectionReading')}</h2>
      <div className="app-settings-control-row flex items-center justify-between gap-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-base font-medium tracking-[-0.01em] text-ink">
            {t('settings.appearance.fontSize')}
          </div>
        </div>
        <Segmented<FontSize>
          value={prefs.fontSize}
          onChange={setFontSize}
          options={[
            { value: 14, label: '14' },
            { value: 15, label: '15' },
            { value: 16, label: '16' },
          ]}
        />
      </div>

      <div className="app-settings-control-row flex items-center justify-between gap-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-base font-medium tracking-[-0.01em] text-ink">
            {t('settings.appearance.railPlacement')}
          </div>
        </div>
        <Segmented<RailPlacement>
          value={railPlacement}
          onChange={setRailPlacement}
          options={[
            { value: 'top', label: t('settings.appearance.railTop') },
            { value: 'left', label: t('settings.appearance.railLeft') },
          ]}
        />
      </div>

      <div className="app-settings-control-row flex items-center justify-between gap-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-base font-medium tracking-[-0.01em] text-ink">
            {t('settings.appearance.reducedMotion')}
          </div>
        </div>
        <Switch
          checked={prefs.reducedMotion}
          onChange={setReducedMotion}
          label={t('settings.appearance.reducedMotion')}
        />
      </div>


    </div>
  );
}

/** Reactive view of the active theme selection (preset id or custom palette). */
function useThemeSelection(): ThemeSelection {
  const [sel, setSel] = useState<ThemeSelection>(getSelection);
  useEffect(() => subscribeTheme(() => setSel(getSelection())), []);
  return sel;
}

/** Compact segmented control — a row of pill buttons with one active. */
